import { useCallback, useState } from "react";

import type { ModelViewerElement } from "@google/model-viewer";
import {
  Button,
  ButtonIconWithLabel,
  Icon_ARKit24,
  Modal,
  Platform,
  PlatformAPI,
  useManagedModal,
} from "@shapr3d/frontend-lib";
import type React from "react";
import QRCode from "react-qr-code";

import { useTracker } from "../../../../hooks/useTracker";
import useTranslation from "../../../../hooks/useTranslation";
import { isARSupportedDevice } from "../../../ModelViewer/useModelViewer";
import { ARNotSupportedModal } from "../ARNotSupportedModal/ARNotSupportedModal";

import style from "./QRCodeModal.module.css";

interface QRCodeModalProps {
  disabled?: boolean;
  modelViewer: React.RefObject<ModelViewerElement | null> | undefined;
  className?: string;
  variant?: "grayscale" | "borderless";
  url?: string;
}

const platform = PlatformAPI.getInstance().getPlatform();
const AR_SUPPORTED_PLATFORMS = [Platform.iPad, Platform.iPhone, Platform.Android];

const QRCodeModal: React.FC<QRCodeModalProps> = ({
  disabled,
  modelViewer,
  className,
  variant = "grayscale",
  url = window.location.href,
}) => {
  const tracker = useTracker();
  const { t, m } = useTranslation();

  const [open, setOpen] = useState(false);
  const arNotSupportedModal = useManagedModal();

  const URLWithQRSource = new URL(url);
  URLWithQRSource.searchParams.set("source", "qr");

  const onARButtonClick = useCallback(() => {
    tracker.trackButtonClick({ elementId: "activate-ar-button" });
    if (isARSupportedDevice()) {
      modelViewer?.current?.activateAR?.();
    } else {
      if (AR_SUPPORTED_PLATFORMS.includes(platform)) {
        arNotSupportedModal.open();
      } else {
        setOpen((open) => !open);
      }
    }
  }, [tracker, modelViewer, arNotSupportedModal]);

  return (
    <>
      <Button className={className} disabled={disabled} variant={variant} onClick={onARButtonClick}>
        <ButtonIconWithLabel icon={<Icon_ARKit24 />} label={t(m.ModelViewer.ArButtonLabel)} />
      </Button>

      <Modal
        title={t(m.ModelViewer.QrCodeModalTitle)}
        open={open}
        onClose={() => setOpen(false)}
        size="small"
      >
        <div className={style.contentContainer}>
          <QRCode size={160} value={URLWithQRSource.href} />
          <div className={style.disclaimer}>{t(m.ModelViewer.QrCodeModalText)}</div>
        </div>
      </Modal>
      <ARNotSupportedModal modalProps={arNotSupportedModal.props} />
    </>
  );
};

export default QRCodeModal;
